import { randomId } from "@excalidraw/common";
import {
  getBoundTextElement,
  getCommonBounds,
  newImageElement,
  newLinearElement,
} from "@excalidraw/element";
import { pointFrom } from "@excalidraw/math";

import type { LocalPoint, Radians } from "@excalidraw/math";
import type { FileId } from "@excalidraw/element/types";
import type {
  ElementsMap,
  ExcalidrawElement,
  ExcalidrawImageElement,
  ExcalidrawLineElement,
  NonDeletedExcalidrawElement,
} from "@excalidraw/element/types";

import { exportToCanvas } from "../scene/export";

import type { AppState, BinaryFileData, BinaryFiles } from "../types";

/** scene px of slack around the element, so soft edges/shadows aren't clipped */
const RASTER_PADDING = 12;
/**
 * Supersampling for the analysis pass. Everything downstream — the contour
 * trace as much as the fallback bake — is only as accurate as this, so it
 * runs above 1:1; 3× puts the trace's staircase well under half a scene px.
 */
const MAX_RASTER_SCALE = 3;
/** ceiling on baked pixels, so erasing a wall-sized shape can't hang the tab */
const MAX_RASTER_PIXELS = 6_000_000;
/** alpha (0-255) at or below which a pixel counts as erased/empty */
const ALPHA_THRESHOLD = 8;
/**
 * Blobs smaller than this (in baked px²) are dropped — an eraser edge leaves
 * a confetti of 1-2px slivers along the cut, and turning each into its own
 * selectable element would be unusable.
 */
const MIN_PIECE_AREA = 24;
/**
 * ponytail: past this many blobs we keep the element whole instead of
 * decomposing. Scribbling the eraser over a big shape can produce hundreds of
 * islands, and a 300-element group helps nobody. Upgrade path: merge the
 * smallest blobs into their nearest neighbour instead of bailing.
 */
const MAX_PIECES = 48;

export type DecomposeResult = {
  /**
   * elements replacing the originals, grouped when there's >1. A piece stays
   * a vector polygon whenever the original rendered in a single colour and
   * the piece has no holes; otherwise it's a baked image (see
   * `soleVisibleColor`).
   */
  pieces: (ExcalidrawLineElement | ExcalidrawImageElement)[];
  /** PNG payloads the pieces reference; must be added before they render */
  files: BinaryFileData[];
  /** ids consumed by the bake (the element plus any bound text) */
  consumedIds: ExcalidrawElement["id"][];
};

/**
 * 8-connected component labelling over the alpha channel.
 * Returns one bounding box per blob plus the label map, both in canvas px.
 */
export const labelBlobs = (
  data: Uint8ClampedArray,
  width: number,
  height: number,
) => {
  const labels = new Int32Array(width * height); // 0 = unvisited/empty
  const boxes: { x0: number; y0: number; x1: number; y1: number }[] = [];
  // one shared stack, reused across blobs — the frontier is at most the
  // canvas size, and reallocating per blob is what makes naive floodfill slow
  const stack = new Int32Array(width * height);

  for (let start = 0; start < width * height; start++) {
    if (labels[start] !== 0 || data[start * 4 + 3] <= ALPHA_THRESHOLD) {
      continue;
    }
    const label = boxes.length + 1;
    const box = {
      x0: start % width,
      y0: (start / width) | 0,
      x1: start % width,
      y1: (start / width) | 0,
    };
    let top = 0;
    stack[top++] = start;
    labels[start] = label;

    while (top > 0) {
      const index = stack[--top];
      const x = index % width;
      const y = (index / width) | 0;

      if (x < box.x0) {
        box.x0 = x;
      }
      if (x > box.x1) {
        box.x1 = x;
      }
      if (y < box.y0) {
        box.y0 = y;
      }
      if (y > box.y1) {
        box.y1 = y;
      }

      for (let dy = -1; dy <= 1; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= height) {
          continue;
        }
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= width || (dx === 0 && dy === 0)) {
            continue;
          }
          const n = ny * width + nx;
          if (labels[n] === 0 && data[n * 4 + 3] > ALPHA_THRESHOLD) {
            labels[n] = label;
            stack[top++] = n;
          }
        }
      }
    }

    boxes.push(box);
  }

  return { labels, boxes };
};

/**
 * Traces the outer boundary of one labelled blob (Moore-neighbour / "radial
 * sweep" contour following) and returns it as a closed ring in canvas px.
 *
 * Only the outer boundary: a `line` element with `polygon: true` is a single
 * ring, so a piece with a hole in it (erasing a dot in the middle of a disc)
 * comes back solid.
 * ponytail: holes need a real path element with subpaths — until then the
 * caller falls back to a raster piece when the blob has interior holes.
 */
export const traceBlob = (
  labels: Int32Array,
  label: number,
  width: number,
  height: number,
  box: { x0: number; y0: number; x1: number; y1: number },
): [number, number][] => {
  const at = (x: number, y: number) =>
    x >= 0 &&
    y >= 0 &&
    x < width &&
    y < height &&
    labels[y * width + x] === label;

  // first pixel of the blob in scan order is guaranteed to be on its boundary
  let startX = -1;
  let startY = -1;
  for (let y = box.y0; y <= box.y1 && startX < 0; y++) {
    for (let x = box.x0; x <= box.x1; x++) {
      if (at(x, y)) {
        startX = x;
        startY = y;
        break;
      }
    }
  }
  if (startX < 0) {
    return [];
  }

  // 8 neighbours, clockwise from "west"
  const N = [
    [-1, 0],
    [-1, -1],
    [0, -1],
    [1, -1],
    [1, 0],
    [1, 1],
    [0, 1],
    [-1, 1],
  ];

  const ring: [number, number][] = [];
  let cx = startX;
  let cy = startY;
  // came from the west (nothing above/left of a scan-order first pixel)
  let dir = 0;
  // a boundary visits at most every pixel twice; the cap is a safety net
  const maxSteps = (box.x1 - box.x0 + 2) * (box.y1 - box.y0 + 2) * 4 + 32;

  for (let step = 0; step < maxSteps; step++) {
    ring.push([cx, cy]);
    let moved = false;
    // start scanning one past the direction we arrived from, so we hug the
    // boundary instead of doubling back
    for (let i = 1; i <= 8; i++) {
      const d = (dir + i + 6) % 8;
      const nx = cx + N[d][0];
      const ny = cy + N[d][1];
      if (at(nx, ny)) {
        cx = nx;
        cy = ny;
        dir = d;
        moved = true;
        break;
      }
    }
    if (!moved) {
      break; // isolated pixel
    }
    if (cx === startX && cy === startY) {
      break;
    }
  }

  return ring;
};

/** Douglas–Peucker, iterative — keeps the ring vector-light without visibly
 *  rounding corners (`eps` is in canvas px). */
export const simplifyRing = (
  ring: readonly [number, number][],
  eps: number,
): [number, number][] => {
  if (ring.length < 3) {
    return [...ring];
  }
  const keep = new Uint8Array(ring.length);
  keep[0] = 1;
  keep[ring.length - 1] = 1;
  const stack: [number, number][] = [[0, ring.length - 1]];

  while (stack.length) {
    const [first, last] = stack.pop()!;
    if (last <= first + 1) {
      continue;
    }
    const [ax, ay] = ring[first];
    const [bx, by] = ring[last];
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;
    let worst = -1;
    let worstIndex = -1;
    for (let i = first + 1; i < last; i++) {
      const [px, py] = ring[i];
      const d = Math.abs(dy * px - dx * py + bx * ay - by * ax) / len;
      if (d > worst) {
        worst = d;
        worstIndex = i;
      }
    }
    if (worst > eps && worstIndex > 0) {
      keep[worstIndex] = 1;
      stack.push([first, worstIndex], [worstIndex, last]);
    }
  }

  const out: [number, number][] = [];
  for (let i = 0; i < ring.length; i++) {
    if (keep[i]) {
      out.push(ring[i]);
    }
  }
  return out;
};

/**
 * The single colour every visible pixel of `element` has, or null when it
 * renders in more than one (a filled shape with a contrasting border, an
 * image, a gradient, anything with effects). Single-colour elements can be
 * re-emitted as filled vector polygons after a cut; multi-colour ones have
 * to be baked to pixels to keep their appearance.
 */
export const soleVisibleColor = (element: ExcalidrawElement): string | null => {
  if (
    element.type === "image" ||
    element.type === "iframe" ||
    element.type === "embeddable" ||
    element.type === "frame" ||
    element.type === "magicframe" ||
    element.backgroundGradient ||
    element.strokeGradient ||
    element.effects
  ) {
    return null;
  }
  const hasFill = element.backgroundColor !== "transparent";
  // hachure/cross-hatch leave gaps that a solid traced polygon would paint
  // over, turning a sketchy fill into a flat one
  if (hasFill && element.fillStyle !== "solid") {
    return null;
  }
  const stroke =
    element.strokeColor === "transparent" || element.strokeWidth === 0
      ? null
      : element.strokeColor;
  const fill = hasFill ? element.backgroundColor : null;

  if (stroke && fill) {
    return stroke === fill ? stroke : null;
  }
  return stroke ?? fill ?? null;
};

/**
 * Does the blob enclose any empty pixel? Flood-fills the background inside
 * the blob's box starting from its border: anything empty and unreachable is
 * a hole. A ring-shaped piece can't be expressed as a single polygon, so the
 * caller rasterizes those instead of tracing them.
 */
export const blobHasHoles = (
  labels: Int32Array,
  label: number,
  width: number,
  box: { x0: number; y0: number; x1: number; y1: number },
): boolean => {
  const w = box.x1 - box.x0 + 3; // 1px transparent apron on each side
  const h = box.y1 - box.y0 + 3;
  const outside = new Uint8Array(w * h);
  const inBlob = (lx: number, ly: number) => {
    const x = box.x0 + lx - 1;
    const y = box.y0 + ly - 1;
    return (
      x >= box.x0 &&
      y >= box.y0 &&
      x <= box.x1 &&
      y <= box.y1 &&
      labels[y * width + x] === label
    );
  };

  const stack = [0];
  outside[0] = 1;
  let reached = 0;
  while (stack.length) {
    const index = stack.pop()!;
    reached++;
    const x = index % w;
    const y = (index / w) | 0;
    const push = (nx: number, ny: number) => {
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) {
        return;
      }
      const n = ny * w + nx;
      if (!outside[n] && !inBlob(nx, ny)) {
        outside[n] = 1;
        stack.push(n);
      }
    };
    push(x - 1, y);
    push(x + 1, y);
    push(x, y - 1);
    push(x, y + 1);
  }

  let blobPixels = 0;
  for (let y = box.y0; y <= box.y1; y++) {
    for (let x = box.x0; x <= box.x1; x++) {
      if (labels[y * width + x] === label) {
        blobPixels++;
      }
    }
  }

  return reached + blobPixels < w * h;
};

/** crops one labelled blob out of `source` into its own PNG data URL */
const cropBlob = (
  source: ImageData,
  labels: Int32Array,
  label: number,
  box: { x0: number; y0: number; x1: number; y1: number },
): string | null => {
  const w = box.x1 - box.x0 + 1;
  const h = box.y1 - box.y0 + 1;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  const out = ctx.createImageData(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const src = (box.y0 + y) * source.width + (box.x0 + x);
      // pixels belonging to a different blob stay transparent, so two blobs
      // whose bounding boxes overlap don't leak into each other
      if (labels[src] !== label) {
        continue;
      }
      const dst = (y * w + x) * 4;
      out.data[dst] = source.data[src * 4];
      out.data[dst + 1] = source.data[src * 4 + 1];
      out.data[dst + 2] = source.data[src * 4 + 2];
      out.data[dst + 3] = source.data[src * 4 + 3];
    }
  }
  ctx.putImageData(out, 0, 0);
  return canvas.toDataURL("image/png");
};

/**
 * Bakes a pixel-erased element into image pieces — one per surviving blob,
 * grouped together when the erase cut it into more than one.
 *
 * Vector shapes have no way to represent "a rectangle with a bite out of
 * it", so a pixel erase on anything that isn't a brush stroke rasterizes,
 * exactly like erasing a vector layer in Procreate/Photoshop does. Brush
 * strokes never come here: their masks ARE their persistent representation.
 *
 * Returns `null` to mean "leave the element alone" (nothing was actually
 * erased, or the cut fragmented past `MAX_PIECES`), and a result with an
 * empty `pieces` array to mean "it's all gone, delete it".
 */
export const decomposeErasedElement = async (
  element: NonDeletedExcalidrawElement,
  elementsMap: ElementsMap,
  appState: AppState,
  files: BinaryFiles,
): Promise<DecomposeResult | null> => {
  if (!element.eraserMasks?.length) {
    return null;
  }

  // a container's label is a separate element that would otherwise survive
  // its container being cut up — bake it into the same pieces
  const boundText = getBoundTextElement(element, elementsMap);
  const sources = (boundText ? [element, boundText] : [element]).map((el) => ({
    ...el,
    // the pieces carry the group; baking group membership into the raster
    // would be meaningless, and frames would clip the bake
    frameId: null,
  })) as NonDeletedExcalidrawElement[];

  const [minX, minY, maxX, maxY] = getCommonBounds(sources);
  const sceneWidth = maxX - minX + RASTER_PADDING * 2;
  const sceneHeight = maxY - minY + RASTER_PADDING * 2;
  if (!(sceneWidth > 0) || !(sceneHeight > 0)) {
    return null;
  }

  const scale = Math.min(
    MAX_RASTER_SCALE,
    Math.sqrt(MAX_RASTER_PIXELS / (sceneWidth * sceneHeight)),
  );

  const rasterize = async (withMasks: boolean) => {
    const canvas = await exportToCanvas(
      withMasks
        ? sources
        : sources.map((el) => ({ ...el, eraserMasks: undefined })),
      {
        ...appState,
        exportScale: scale,
        // bake what the user is looking at right now — the dark-mode filter
        // is applied to vector elements at draw time but never to images, so
        // a light-baked piece would suddenly glow on a dark canvas.
        // ponytail: pieces therefore don't re-theme; re-erase to re-bake.
        exportWithDarkMode: appState.theme === "dark",
      },
      files,
      {
        exportBackground: false,
        exportPadding: RASTER_PADDING,
        viewBackgroundColor: "transparent",
      },
    );
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx || !canvas.width || !canvas.height) {
      return null;
    }
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  };

  const opaqueCount = (data: ImageData) => {
    let n = 0;
    for (let i = 3; i < data.data.length; i += 4) {
      if (data.data[i] > ALPHA_THRESHOLD) {
        n++;
      }
    }
    return n;
  };

  const image = await rasterize(true);
  if (!image) {
    return null;
  }

  // The eraser hit-test is deliberately generous (it works off the stroke's
  // bounding path, not its painted pixels), so a gesture that merely passes
  // near a shape can leave a mask that removes nothing. Rasterizing on that
  // would silently convert an untouched vector shape into an image, so
  // confirm real pixel loss before committing to the bake.
  const clean = await rasterize(false);
  if (!clean || opaqueCount(clean) === opaqueCount(image)) {
    return null;
  }

  const { labels, boxes } = labelBlobs(image.data, image.width, image.height);

  const kept = boxes
    .map((box, i) => ({ box, label: i + 1 }))
    .filter(
      ({ box }) =>
        (box.x1 - box.x0 + 1) * (box.y1 - box.y0 + 1) >= MIN_PIECE_AREA,
    );

  if (kept.length > MAX_PIECES) {
    return null;
  }

  const consumedIds = sources.map((el) => el.id);

  if (kept.length === 0) {
    return { pieces: [], files: [], consumedIds };
  }

  // canvas (0,0) is this scene point — see exportToCanvas, which renders with
  // scrollX = -minX + exportPadding at `scale`
  const originX = minX - RASTER_PADDING;
  const originY = minY - RASTER_PADDING;

  const groupId = kept.length > 1 ? randomId() : null;
  const pieces: DecomposeResult["pieces"] = [];
  const outFiles: BinaryFileData[] = [];
  const groupIds = groupId
    ? [...element.groupIds, groupId]
    : [...element.groupIds];
  // rotation and opacity are already baked into the traced/rasterized pixels
  const shared = {
    angle: 0 as Radians,
    opacity: 100,
    frameId: element.frameId,
    locked: element.locked,
    groupIds,
  };

  // vector re-emission needs the element to render in exactly one colour and
  // the blob to have no interior holes — see soleVisibleColor / traceBlob
  const vectorColor = boundText ? null : soleVisibleColor(element);

  for (const { box, label } of kept) {
    const bw = box.x1 - box.x0 + 1;
    const bh = box.y1 - box.y0 + 1;

    if (vectorColor && !blobHasHoles(labels, label, image.width, box)) {
      const ring = simplifyRing(
        traceBlob(labels, label, image.width, image.height, box),
        // ~0.4 scene px: below what's visible, well above the staircase the
        // raster trace introduces
        Math.max(0.75, 0.4 * scale),
      );
      if (ring.length >= 3) {
        pieces.push(
          newLinearElement({
            type: "line",
            polygon: true,
            ...shared,
            x: originX + box.x0 / scale,
            y: originY + box.y0 / scale,
            width: bw / scale,
            height: bh / scale,
            // the traced outline already includes the original stroke's
            // width, so painting it as a fill (and not stroking it again)
            // reproduces the shape instead of fattening it
            strokeColor: "transparent",
            backgroundColor: vectorColor,
            fillStyle: "solid",
            strokeWidth: element.strokeWidth,
            strokeStyle: element.strokeStyle,
            roughness: 0,
            roundness: null,
            points: ring.map(([px, py]) =>
              pointFrom<LocalPoint>(
                (px - box.x0) / scale,
                (py - box.y0) / scale,
              ),
            ),
          }) as DecomposeResult["pieces"][number],
        );
        continue;
      }
    }

    const dataURL = cropBlob(image, labels, label, box);
    if (!dataURL) {
      continue;
    }
    const fileId = randomId() as FileId;
    outFiles.push({
      id: fileId,
      dataURL: dataURL as BinaryFileData["dataURL"],
      mimeType: "image/png",
      created: Date.now(),
      lastRetrieved: Date.now(),
    });

    pieces.push(
      newImageElement({
        type: "image",
        fileId,
        status: "saved",
        ...shared,
        x: originX + box.x0 / scale,
        y: originY + box.y0 / scale,
        width: bw / scale,
        height: bh / scale,
      }),
    );
  }

  return pieces.length ? { pieces, files: outFiles, consumedIds } : null;
};
